<template>
    <wrapper-features-templates-operations
    fullWhiteBox
    nonAuthenticatedFeature
    hideClose
    showFooter
    :fullHeight="activeStep === 3"
    :customBreadcrumbs="{ currentArea: t('initialActions.firstLogin.breadcrumb') }"
    :showOverlayLoader="showOverlayLoader"
    :scrollValue="scrollValue"
    :showStepper="activeStep <= stepperParams.length"
    :stepperParams="stepperParams"
    :activeStep="activeStep"
    :footerButtonsParams="footerButtonsParams.filter(obj => obj.visible)"
    :buttonFullWidth="buttonFullWidth"
    :tokenParams="{ newPassword: formData.newPassword, oldPassword: formData.oldPassword }"
    :tokenTitleAppend="t('userArea.definitions.passwordChange.tokenTitleAppend')"
    @challengeSuccessHandler="successHandler($event)"
    @challengeErrorHandler="errorHandler($event, changePassword)"
    @clearScrollToValue="scrollValue = null">
        <!-- *********************************************
        *												 *
        *                      Content                   *
        *												 *
        ********************************************** -->
        <component
        :is="getActiveStepComponent"
        :formData="formData"
        :mandatoryList="mandatoryList"
        :showMandatoryErrors="showMandatoryErrors"
        :clearFormErrors="clearFormErrors"
        @formDataChange="formDataChangeHandler"
        @hasInvalidFields="hasInvalidFields = $event"
        @resetShowMandatoryErrorsProp="showMandatoryErrors = false"
        @formErrorsCleared="clearFormErrors = false"
        @hasProfilePicture="hasProfilePicture = $event"
        @scrollTo="scrollValue = $event"/>
    </wrapper-features-templates-operations>
    <!-- Feedback Toasts -->
    <feedback-toast
    v-if="showFeedbackToast"
    :toastType="feedbackToastData.toastType"
    :feedbackText="feedbackToastData.text"
    :buttonsParams="feedbackToastData.buttons"
    :triggerClose="triggerClose"
    @hideMe="[showFeedbackToast = false, triggerClose = false]"/>
</template>

<script setup>
import WrapperFeaturesTemplatesOperations from '@/components/wrappers/featuresTemplates/Operations'
import FeedbackToast from '@/components/generic/toasts/Generic'

/* Vue */
import { ref, computed, defineAsyncComponent } from 'vue'

/* Store */
import { mapActions, mapGetters } from '@/store/helpers/mappers'
import { moduleMapper } from '@/store/helpers/modules.map'

/* Assets & Helpers */
import { navigateByPath } from '@/router/helpers/navigation'
import { routeList } from '@/router/helpers/routes'
import { errorObjectConstructor, sucessObjectConstrutor, emptyObjectConstructor } from '@/assets/scripts/toastsHelpers'
import { atLeastOneIsEmpty, clearObjectStrings } from '@/assets/scripts/objects'

/* i18n */
import { useI18n } from 'vue-i18n'
const { t } = useI18n()

/* Emmitters */
const emit = defineEmits(['updateLoginLoader'])

/* mapActions && mapGetters */
const { modifyPassword, changeUserFriendlyName, updateInitialActions } = mapActions(moduleMapper.SESSION)
const { getMobileAppState } = mapGetters()

/* Show Data */
const showOverlayLoader = ref(false)

/* Generic Data */
const scrollValue = ref(null)

/*************************************************
*                                                *
*                    Steps Data                  *
*                                                *
*************************************************/
const activeStep = ref(1)
const stepperParams = ref([
    { description: t('initialActions.firstLogin.stepper.password') },
    { description: t('initialActions.firstLogin.stepper.username') },
    { description: t('initialActions.firstLogin.stepper.photo') },
    { description: t('initialActions.firstLogin.stepper.info') }
])

const getActiveStepComponent = computed(() => {
    const stepNumber = activeStep.value
    return defineAsyncComponent(() => import('@/components/features/initialActions/firstLogin/Step' + stepNumber))
})

/*************************************************
*                                                *
*                 Form Logic Data                *
*                                                *
*************************************************/
const hasProfilePicture = ref(false)
const showMandatoryErrors = ref(false)
const hasInvalidFields = ref(false)
const clearFormErrors = ref(false)
const tempFriendlyUsername = ref(null)

const formData = ref({
    oldPassword: '',
    newPassword: '',
    confirmNewPassword: '',
    friendlyUsername: ''
})

const mandatoryList = ref([
    'oldPassword',
    'newPassword',
    'confirmNewPassword'
])

const formDataChangeHandler = ({ field, inputValue }) => {
    formData.value[field] = inputValue
}

/*************************************************
*                                                *
*               Footer/Buttons Logic             *
*                                                *
*************************************************/
/* Footer Buttons Data */
const footerButtonsParams = computed(() => {
    return [{
        label: t('initialActions.firstLogin.footerButtons.clear'),
        buttonPosition: 'left',
        visible: activeStep.value === 1,
        callbackFunction: () => { clearInputs() }
    }, {
        label: t('initialActions.firstLogin.footerButtons.back'),
        buttonPosition: !getMobileAppState.value ? 'right' : 'left',
        icon: 'arrow-left-full',
        visible: activeStep.value === 3,
        callbackFunction: () => {
            stepBack()
            if (showFeedbackToast.value) triggerClose.value = true
        }
    }, {
        label: t('initialActions.firstLogin.footerButtons.forward'),
        buttonType: 'primary',
        buttonPosition: 'right',
        icon: 'arrow-right-full',
        iconPosition: 'right',
        visible: showForwardButton.value,
        callbackFunction: () => { validateSteps() }
    }, {
        label: t('initialActions.firstLogin.footerButtons.skip'),
        buttonType: 'primary',
        buttonPosition: 'right',
        icon: 'arrow-right-full',
        iconPosition: 'right',
        visible: showSkipButton.value,
        callbackFunction: () => { stepForward() }
    }, {
        label: t('initialActions.firstLogin.footerButtons.enter'),
        buttonType: 'primary',
        icon: 'login',
        iconPosition: 'right',
        visible: activeStep.value === 4,
        callbackFunction: () => { proceedToEnterApp() }
    }]
})

const buttonFullWidth = computed(() => {
    if (getMobileAppState.value) return activeStep.value === 2 || activeStep.value === 4

    return activeStep.value === 4
})

/* Controller to show forward button */
const showForwardButton = computed(() => {
    return activeStep.value === 1 || (activeStep.value === 2 && !!formData.value.friendlyUsername.length) || (activeStep.value === 3 && hasProfilePicture.value)
})

/* Controller to show forward button */
const showSkipButton = computed(() => {
    return (activeStep.value === 2 && !formData.value.friendlyUsername.length) || (activeStep.value === 3 && !hasProfilePicture.value)
})

/* Advance to next step */
const stepForward = () => {
    activeStep.value++
    scrollValue.value = 0
}

/* Go to previous step */
const stepBack = () => {
    activeStep.value--
    scrollValue.value = 0
}

/* Controls step validation */
const validateSteps = () => {
    switch (activeStep.value) {
        case 1:
            validateStep1()
            break
        case 2:
            validateStep2()
            break
        case 3:
            stepForward()
            break
    }
}

const validateStep1 = () => {
    const emptyField = atLeastOneIsEmpty(formData.value, mandatoryList.value)

    if (emptyField || hasInvalidFields.value) {
        showMandatoryErrors.value = true
        return
    }

    changePassword()
}

const validateStep2 = () => {
    if (hasInvalidFields.value) return

    if (formData.value.friendlyUsername === tempFriendlyUsername.value) {
        stepForward()
    } else {
        changeFriendlyUsername()
    }
}

/* Updates initial actions and redirects to home */
const proceedToEnterApp = () => {
    updateInitialActions('FIRST_LOGIN')
    updateInitialActions('FIRST_LOGIN_AFTER_REGISTRATION')
    updateInitialActions('CHANGE_PASSWORD_AFTER_RESET')
    !getMobileAppState.value ? navigateByPath(routeList.HOME_PATH) : navigateByPath(routeList.HOME_MOBILE_PATH)
}

const clearInputs = () => {
    clearObjectStrings(formData.value)
    clearFormErrors.value = true
    hasInvalidFields.value = false
}

/*************************************************
*                                                *
*            Change Password Request             *
*                                                *
*************************************************/
const changePassword = async () => {
    showOverlayLoader.value = true

    const payload = {
        oldPassword: formData.value.oldPassword,
        newPassword: formData.value.newPassword
    }

    try {
        const res = await modifyPassword(payload)
        showOverlayLoader.value = false

        if (!res.data.challenge) successHandler(res.data)
    } catch (error) {
        if (error.toString() === 'Cancel') return
        errorHandler(error.response.data, changePassword)
    }
}

/*************************************************
*                                                *
*        Change Friendly Username Request        *
*                                                *
*************************************************/
const changeFriendlyUsername = async () => {
    showOverlayLoader.value = true

    const payload = {
        friendlyUsername: formData.value.friendlyUsername
    }

    try {
        const res = await changeUserFriendlyName(payload)
        showOverlayLoader.value = false
        tempFriendlyUsername.value = formData.value.friendlyUsername
        successHandler(res.data)
    } catch (error) {
        if (error.toString() === 'Cancel') return
        errorHandler(error.response.data, changeFriendlyUsername)
    }
}

/*************************************************
*                                                *
*             Success/Error Handlers             *
*                                                *
*************************************************/
const showFeedbackToast = ref(false)
const triggerClose = ref(false)
const feedbackToastData = ref(emptyObjectConstructor())

const successHandler = (data) => {
    feedbackToastData.value = sucessObjectConstrutor(data.message)
	showFeedbackToast.value = true
    stepForward()
}

const errorHandler = (data, retryFn) => {
    showOverlayLoader.value = false

	feedbackToastData.value = errorObjectConstructor(data, retryFn)
	showFeedbackToast.value = true
}

emit('updateLoginLoader', false)
</script>

<style lang="scss" scoped>
</style>
